import { OrganizationDetailFragment } from '@alice-financial/alice-sdk'
import { Grid } from '@alice-financial/pretext-ui'
import * as React from 'react'
import { useLaunchDateStatus } from '../../organization/onboarding/useLaunchDateStatus'
import { useOrderFormStatus } from '../../organization/onboarding/useOrderFormStatus'
import { useKYCStatus } from '../aliceCardProgram/KYC/useKYCStatus'
import { useAliceCardProgramStatus } from '../aliceCardProgram/useAliceCardProgramStatus'
import { useBillingStatus } from '../billing/useBillingStatus'
import { useCompanyProfileStatus } from '../companyProfile/useCompanyProfileStatus'
import { usePayrollStatus } from '../payroll/usePayrollStatus'
import { useShiftsStatus } from '../shifts/useShiftsStatus'
import { useEnrollmentSuccessStatus } from '../survey/enrollmentSuccess/useEnrollmentSuccessStatus'
import { HealthcheckRowData, HealthcheckType } from '../types'
import { useGetNavigationPath } from '../utils/healthcheckPaths'
import { useLocationsStatus } from '../workLocations/useLocationsStatus'
import { LaunchDateInput } from './LaunchDateInput'

export const useHealthcheckRowsData = (org: OrganizationDetailFragment): HealthcheckRowData[] => {
  const orderFormStatus = useOrderFormStatus(org.onboardingWorkflow)
  const kycStatus = useKYCStatus(org, org.onboardingWorkflow)
  const shiftsStatus = useShiftsStatus(org, org.onboardingWorkflow)
  const companyProfileStatus = useCompanyProfileStatus(org, org.onboardingWorkflow)
  const payrollStatus = usePayrollStatus(org, org.onboardingWorkflow)
  const locationsStatus = useLocationsStatus(org, org.onboardingWorkflow)
  const billingStatus = useBillingStatus(org, org.onboardingWorkflow)
  const aliceCardProgramStatus = useAliceCardProgramStatus(org, org.onboardingWorkflow)
  const enrollmentSuccessStatus = useEnrollmentSuccessStatus(org)
  const launchDateStatus = useLaunchDateStatus(org)

  // Navigation path function
  const getNavigationPath = useGetNavigationPath(org)

  // Transform status results into row data
  const rows: HealthcheckRowData[] = []
  const isOnboarded = org.isOnboarded

  if (orderFormStatus && !isOnboarded) {
    rows.push({
      id: 'order-form',
      healthcheckType: HealthcheckType.OrderForm,
      status: orderFormStatus.status,
      primary: orderFormStatus.primary,
      secondary: orderFormStatus.secondary,
      healthcheckTitle: orderFormStatus.healthcheckTitle || 'Order form',
      assignee: orderFormStatus.assignee,
      navigationPath: getNavigationPath(HealthcheckType.OrderForm, orderFormStatus),
      specialAction: null,
      org,
    })
  }

  if (locationsStatus) {
    rows.push({
      id: 'locations',
      healthcheckType: HealthcheckType.WorkLocations,
      status: locationsStatus.status,
      primary: locationsStatus.primary,
      secondary: locationsStatus.secondary,
      healthcheckTitle: locationsStatus.healthcheckTitle || 'Locations',
      assignee: locationsStatus.assignee,
      navigationPath: getNavigationPath(HealthcheckType.WorkLocations, locationsStatus),
      specialAction: null,
      org,
    })
  }

  if (companyProfileStatus && !isOnboarded) {
    rows.push({
      id: 'company-profile',
      healthcheckType: HealthcheckType.CompanyProfile,
      status: companyProfileStatus.status,
      primary: companyProfileStatus.primary,
      secondary: companyProfileStatus.secondary,
      healthcheckTitle: companyProfileStatus.healthcheckTitle || 'Company roles',
      assignee: companyProfileStatus.assignee,
      navigationPath: getNavigationPath(HealthcheckType.CompanyProfile, companyProfileStatus),
      specialAction: null,
      org,
    })
  }

  if (kycStatus && !isOnboarded) {
    rows.push({
      id: 'kyc',
      healthcheckType: HealthcheckType.CompleteKyc,
      status: kycStatus.status,
      primary: kycStatus.primary,
      secondary: kycStatus.secondary,
      healthcheckTitle: kycStatus.healthcheckTitle || 'Company Representative',
      assignee: kycStatus.assignee,
      navigationPath: getNavigationPath(HealthcheckType.CompleteKyc, kycStatus),
      specialAction: null,
      org,
    })
  }

  if (payrollStatus && !isOnboarded) {
    rows.push({
      id: 'payroll',
      healthcheckType: HealthcheckType.ConnectPayroll,
      status: payrollStatus.status,
      primary: payrollStatus.primary,
      secondary: payrollStatus.secondary,
      healthcheckTitle: payrollStatus.healthcheckTitle || 'Payroll',
      assignee: payrollStatus.assignee,
      navigationPath: getNavigationPath(HealthcheckType.ConnectPayroll, payrollStatus),
      specialAction: null,
      org,
    })
  }

  if (shiftsStatus) {
    rows.push({
      id: 'shifts',
      healthcheckType: HealthcheckType.ConnectShifts,
      status: shiftsStatus.status,
      primary: shiftsStatus.primary,
      secondary: shiftsStatus.secondary,
      healthcheckTitle: shiftsStatus.healthcheckTitle || 'Shift scheduling',
      assignee: shiftsStatus.assignee,
      navigationPath: getNavigationPath(HealthcheckType.ConnectShifts, shiftsStatus),
      specialAction: null,
      org,
    })
  }

  if (billingStatus) {
    rows.push({
      id: 'billing',
      healthcheckType: HealthcheckType.ConnectBilling,
      status: billingStatus.status,
      primary: billingStatus.primary,
      secondary: billingStatus.secondary,
      healthcheckTitle: billingStatus.healthcheckTitle || 'Billing',
      assignee: billingStatus.assignee,
      navigationPath: getNavigationPath(HealthcheckType.ConnectBilling, billingStatus),
      specialAction: null,
      org,
    })
  }

  if (aliceCardProgramStatus) {
    rows.push({
      id: 'alice-card-program',
      healthcheckType: HealthcheckType.ConnectBanking,
      status: aliceCardProgramStatus.status,
      primary: aliceCardProgramStatus.primary,
      secondary: aliceCardProgramStatus.secondary,
      healthcheckTitle: aliceCardProgramStatus.healthcheckTitle || 'Contributions Account',
      assignee: aliceCardProgramStatus.assignee,
      navigationPath: getNavigationPath(HealthcheckType.ConnectBanking, aliceCardProgramStatus),
      specialAction: null,
      org,
    })
  }

  if (enrollmentSuccessStatus && !isOnboarded) {
    rows.push({
      id: 'enrollment-success',
      healthcheckType: HealthcheckType.EnrollmentSuccess,
      status: enrollmentSuccessStatus.status,
      primary: enrollmentSuccessStatus.primary,
      secondary: enrollmentSuccessStatus.secondary,
      healthcheckTitle: 'Enrollment success',
      assignee: null, // No assignment support
      navigationPath: getNavigationPath(HealthcheckType.EnrollmentSuccess, enrollmentSuccessStatus),
      specialAction: null,
      org,
    })
  }

  if (launchDateStatus) {
    rows.push({
      id: 'launch-date',
      healthcheckType: HealthcheckType.SetLaunchDate,
      status: launchDateStatus.status,
      primary: launchDateStatus.primary,
      secondary: launchDateStatus.secondary,
      healthcheckTitle: launchDateStatus.primary,
      assignee: null, // No assignment support
      navigationPath: null,
      specialAction: (
        <Grid container alignItems="center" flexWrap="nowrap">
          <LaunchDateInput org={org} />
        </Grid>
      ),
      org,
    })
  }

  return rows
}
